/**
 * Global briefs list — every brief across every project, newest first.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Notebook, Search } from 'lucide-react';
import type { Brief, Project } from '@seo/types';
import { Page, PageHeader, PageToolbar } from '@/components/ui/page';
import { DataTable } from '@/components/data-table';
import { EmptyState } from '@/components/ui/empty';
import { ErrorBanner } from '@/components/error-banner';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { StatusPill } from '@/components/status-pill';
import { useBriefs } from '@/features/briefs/use-briefs';
import { useProjects } from '@/features/projects/use-projects';
import { BriefCreateButton } from '@/features/briefs/components/brief-create-button';
import { relTime } from '@/lib/format';

export function BriefsRoute(): JSX.Element {
  const navigate = useNavigate();
  const briefs = useBriefs();
  const projects = useProjects();
  const [query, setQuery] = useState('');

  const projectsById = useMemo(() => {
    const m = new Map<string, Project>();
    for (const p of projects.data?.items ?? []) m.set(p.id, p);
    return m;
  }, [projects.data]);

  const projectList = projects.data?.items ?? [];

  const rows = useMemo(() => {
    const items = briefs.data?.items ?? [];
    const q = query.trim().toLowerCase();
    const sorted = [...items].sort((a, b) =>
      (b.created_at ?? '').localeCompare(a.created_at ?? ''),
    );
    if (!q) return sorted;
    return sorted.filter((b) => {
      const p = projectsById.get(b.project_id);
      return (
        b.target_keyword?.toLowerCase().includes(q) ||
        b.status.toLowerCase().includes(q) ||
        (p?.name.toLowerCase().includes(q) ?? false)
      );
    });
  }, [briefs.data, projectsById, query]);

  return (
    <Page>
      <PageHeader
        title="Content briefs"
        description="Ready-to-use article plans: outline, headings, meta tags, and internal links your writer (or AI) can build from."
        actions={
          <BriefCreateButton
            projects={projectList.map((p) => ({
              id: p.id,
              name: p.name,
              domain: p.domain,
            }))}
            buttonProps={{ size: 'sm' }}
          />
        }
      />

      <PageToolbar>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by keyword, status, or project..."
            className="h-9 pl-8"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {rows.length} of {briefs.data?.items.length ?? 0}
        </span>
      </PageToolbar>

      {briefs.error ? <ErrorBanner error={briefs.error} /> : null}

      <DataTable<Brief>
        onRowClick={(b) =>
          void navigate({ to: '/briefs/$briefId', params: { briefId: b.id } })
        }
        loading={briefs.isLoading}
        rows={rows}
        rowKey={(b) => b.id}
        columns={[
          {
            key: 'status',
            header: 'Status',
            cell: (b) => <StatusPill status={b.status} />,
            className: 'w-32',
          },
          {
            key: 'keyword',
            header: 'Target keyword',
            cell: (b) => (
              <span className="truncate text-sm font-medium">
                {b.target_keyword?.trim() || `Brief ${b.id.slice(0, 8)}`}
              </span>
            ),
          },
          {
            key: 'project',
            header: 'Project',
            cell: (b) => {
              const p = projectsById.get(b.project_id);
              return (
                <span className="text-sm text-muted-foreground">
                  {p?.name ?? b.project_id.slice(0, 8)}
                </span>
              );
            },
          },
          {
            key: 'delivery',
            header: 'Send to',
            cell: (b) =>
              b.delivery_channel ? (
                <Badge variant="outline" className="font-mono text-[10px]">
                  {b.delivery_channel}
                </Badge>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              ),
            align: 'right',
            className: 'w-32',
          },
          {
            key: 'when',
            header: 'When',
            cell: (b) => (
              <span className="text-xs text-muted-foreground">
                {relTime(b.created_at)}
              </span>
            ),
            align: 'right',
            className: 'w-32',
          },
          {
            key: 'open',
            header: '',
            cell: (b) => (
              <Link
                to="/briefs/$briefId"
                params={{ briefId: b.id }}
                className="text-xs font-medium text-primary hover:underline"
              >
                Open →
              </Link>
            ),
            align: 'right',
            className: 'w-20',
          },
        ]}
        emptyState={
          <EmptyState
            icon={Notebook}
            title="Write a brief for your writer"
            description="Give us a target keyword and we'll compose a plan a writer (or AI) can build from."
            action={
              <BriefCreateButton
                projects={projectList.map((p) => ({
                  id: p.id,
                  name: p.name,
                  domain: p.domain,
                }))}
                buttonProps={{ size: 'sm' }}
              />
            }
          />
        }
      />
    </Page>
  );
}
